import React, {
    Component,
    FC,
    createRef,
    ReactNode,
    KeyboardEvent,
    RefObject,
} from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import _round from 'lodash/round';
import cx from 'classnames';

import { formatDollarsByRange } from '@trqk/utils';

interface Props {
    addClasses?: string;
    chart: am4charts.PieChart | null;
    doToggleSlice?: boolean;
    handleClickOutside?(): void;
    height: number;
    maxWidth?: number;
    pieSeries: am4charts.PieSeries | null;
}

interface State {
    chartIsReady: boolean;
}

const legendDivId = 'earnings-per-show-legend';

const ranges: FormatDollarsRange[] = [
    {
        max: 10,
        style: 'standard',
        precision: 2,
    },
    {
        max: 1000,
        style: 'standard',
        precision: 2,
    },
    {
        max: Infinity,
        style: 'compact',
        precision: 2,
    },
];

export class EarningsPieLegend extends Component<Props, State> {
    private legendDiv: RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);

        this.blurSlice = this.blurSlice.bind(this);
        this.createLegendItem = this.createLegendItem.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.hoverSlice = this.hoverSlice.bind(this);
        this.populateLegendItems = this.populateLegendItems.bind(this);
        this.toggleSlice = this.toggleSlice.bind(this);

        this.legendDiv = createRef();
        this.state = { chartIsReady: false };
    }

    componentDidUpdate(prevProps: Props): void {
        const { chart } = this.props;

        if (prevProps.chart === null && chart !== null && chart.events) {
            chart.events.on('ready', (): void => {
                this.setState({ chartIsReady: true });
            });
        }
    }

    handleKeyDown(e: KeyboardEvent, index: number): void {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.toggleSlice(index);
        }
    }

    blurSlice(index: number): void {
        const { pieSeries } = this.props;

        if (pieSeries !== null) {
            const slice = pieSeries.slices.getIndex(index);

            if (slice !== undefined) {
                slice.isHover = false;
            }
        }
    }

    hoverSlice(index: number): void {
        const { pieSeries } = this.props;

        if (pieSeries !== null) {
            const slice = pieSeries.slices.getIndex(index);

            if (slice !== undefined) {
                slice.isHover = true;
            }
        }
    }

    toggleSlice(index: number): void {
        const { doToggleSlice = true, pieSeries } = this.props;

        if (doToggleSlice && pieSeries !== null) {
            const slice = pieSeries.dataItems.getIndex(index);

            if (slice !== undefined) {
                if (slice.visible) {
                    slice.hide();
                } else {
                    slice.show();
                }
            }
        }
    }

    populateLegendItems(): ReactNode {
        const { pieSeries } = this.props;
        const legendItems = [] as FC[];

        if (pieSeries !== null) {
            pieSeries.dataItems.each((dataItem, index: number) => {
                const LegendItem = this.createLegendItem(dataItem, index);

                if (LegendItem !== null) {
                    legendItems.push(LegendItem);
                }
            });
        }

        return legendItems.map(LegendItem => LegendItem({}));
    }

    createLegendItem(
        { values, value, category }: am4charts.PieSeriesDataItem,
        index: number,
    ): FC | null {
        const { chart } = this.props;
        const color =
            chart === null
                ? am4core.color('#000')
                : chart.colors.getIndex(index);
        const percent = _round(values.value.percent, 2);
        const formattedValue = formatDollarsByRange({
            value,
            ranges,
        });

        const LegendItem: FC = () => (
            <button
                className="legend-item font-bold m-3 block w-full"
                key={`legend-item-${category}`}
                onClick={(): void => {
                    this.toggleSlice(index);
                }}
                onKeyDown={(e: KeyboardEvent): void => {
                    this.handleKeyDown(e, index);
                }}
                onMouseOver={(): void => {
                    this.hoverSlice(index);
                }}
                onFocus={(): void => {
                    this.hoverSlice(index);
                }}
                onMouseOut={(): void => {
                    this.blurSlice(index);
                }}
                onBlur={(): void => {
                    this.blurSlice(index);
                }}
                // This line is taken directly from an amCharts example, so they must have a
                // type definition error.
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                style={{ color }}
                type="button"
            >
                <div
                    className="grid align-items-start justify-items-start w-full"
                    style={{
                        gridTemplateColumns: '20px auto',
                    }}
                >
                    <div
                        className="border border-solid border-tertiary-300 w-3 h-3 mr-1 mt-1"
                        // This line is taken directly from an amCharts example, so they must
                        // have a type definition error.
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        style={{ backgroundColor: color }}
                    />
                    <div className="clamp-2-lines pr-4 text-left">
                        {category}
                    </div>
                    <div />
                    <div className="text-sm font-normal text-tertiary-500 text-left pr-2">
                        {`${formattedValue} (${percent}%)`}
                    </div>
                </div>
            </button>
        );

        return LegendItem;
    }

    render(): JSX.Element {
        const { addClasses, height, maxWidth } = this.props;
        const { chartIsReady } = this.state;

        return (
            <div
                id={legendDivId}
                className={cx(
                    'border border-solid border-tertiary-100 rounded-md overflow-y-auto overflow-x-hidden',
                    addClasses,
                )}
                ref={this.legendDiv}
                style={{
                    height,
                    maxWidth,
                }}
            >
                {chartIsReady && this.populateLegendItems()}
            </div>
        );
    }
}
